/* eslint-disable no-undef */
/* eslint-disable no-param-reassign */
import axios from 'axios';
import SessionAPI from '../../services/SessionAPI';
import router from '../../router';

// filter initial state
const initialFilter = {
  limit: 25,
  page: 1,
  sort: '',
  dir: '',
  rows: 0,
  status: null,
  search: null,
  network: null,
  sponsoredByType: null,
  distributedToType: null,
  active: null,
  type: null,
  paginationFrom: null,
  paginationTo: null,
  promotional: null,
  range: null,
};

// initial state
const state = {
  authenticated: false,
  id: null,
  firstName: null,
  lastName: null,
  email: null,
  phone: null,
  smsEnabled: false,
  sidebarActive: true,
  filters: initialFilter,
  filterPage: null,
  brandIds: [],
  hubIds: [],
  kitchenIds: [],
  organizationIds: [],
  loading: true,
  noAccess: false,
  isMobile: false,
  canRefundOrders: false,
};

// getters
const getters = {
  isAuthenticated: (store) => {
    return store.authenticated;
  },
  firstName: (store) => {
    return store.firstName;
  },
  lastName: (store) => {
    return store.lastName;
  },
  email: (store) => {
    return store.email;
  },
  phone: (store) => {
    return store.phone;
  },
  smsEnabled: (store) => {
    return store.smsEnabled;
  },
  brandCount: (store) => {
    return store.brandIds.length;
  },
  brandAdmin: (store) => {
    return store.brandIds.length > 0;
  },
  hubCount: (store) => {
    return store.hubIds.length;
  },
  hubAdmin: (store) => {
    return store.hubIds.length > 0;
  },
  hubIds: (store) => {
    return store.hubIds;
  },
  kitchenCount: (store) => {
    return store.kitchenIds.length;
  },
  kitchenIds: (store) => {
    return store.kitchenIds;
  },
  kitchenAdmin: (store) => {
    return store.kitchenIds.length > 0;
  },
  organizationCount: (store) => {
    return store.organizationIds.length;
  },
  organizationAdmin: (store) => {
    return store.organizationIds.length > 0;
  },
  organizationIds: (store) => {
    return store.organizationIds;
  },
  sidebarActive: (store) => {
    return store.sidebarActive;
  },
  filters: (store) => {
    return store.filters;
  },
  brandIds: (store) => {
    return store.brandIds;
  },
  filterPage: (store) => {
    return store.filterPage;
  },
  sessionLoading: (store) => {
    return store.loading;
  },
  isAdmin: () => {
    const role = $cookies.isKey('zdm_role') ? $cookies.get('zdm_role') : null;

    return role === 'admin';
  },
  isFacilityAdmin: () => {
    const role = $cookies.isKey('zdm_role') ? $cookies.get('zdm_role') : null;

    return ['admin', 'facility_admin'].includes(role);
  },
  hasNoAccess: (store) => {
    return store.noAccess;
  },
  isMobile: (store) => {
    return store.isMobile;
  },
  canRefundOrders: (store) => {
    return store.canRefundOrders;
  },
};

// actions
const actions = {
  load({ commit }) {
    commit('sessionLoading');
    SessionAPI.getAccount().then((response) => {
      if (response.data) {
        commit('updateUser', response.data);
        commit('sessionLoaded');
      } else {
        commit('sessionLoaded');
        commit('resetUser');
      }
    });
  },
  getAccount({ commit }) {
    commit('sessionLoading');
    SessionAPI.getAccount().then((response) => {
      commit('updateUser', response.data);
      commit('sessionLoaded');
      router.push('/');
    });
  },
  loggedIn({ dispatch }, payload) {
    $cookies.set('zdm_authentication', payload);
    axios.defaults.headers.common.Authorization = `Bearer ${payload}`;
    dispatch('getAccount');
  },
  loggedOut({ commit }) {
    $cookies.remove('zdm_authentication');
    $cookies.remove('zdm_role');
    axios.defaults.headers.common.Authorization = null;
    commit('resetUser');
    router.push('/login');
  },
  updateAccount({ commit }, payload) {
    commit('updateUser', payload);
  },
  toggleSidebar({ commit }) {
    commit('toggleSidebar');
  },
  hideSidebar({ commit }) {
    commit('hideSidebar');
  },
  updateFilterPage({ commit }, payload) {
    commit('updateFilterPage', payload);
  },
  updateFilters({ commit }, payload) {
    commit('updateFilters', payload);
  },
  resetFilters({ commit }) {
    commit('resetFilters');
  },
  resetNoAccess({ commit }) {
    commit('resetNoAccess');
  },
  updateIsMobile({ commit }, payload) {
    commit('updateIsMobile', payload);
  },
};

// mutations
const mutations = {
  updateIsMobile(store, data) {
    store.isMobile = data;
  },
  sessionLoading(store) {
    store.loading = true;
  },
  sessionLoaded(store) {
    store.loading = false;
  },
  resetUser(store) {
    store.id = null;
    store.firstName = null;
    store.lastName = null;
    store.email = null;
    store.phone = null;
    store.smsEnabled = false;
    store.authenticated = false;
    store.kitchenCount = 0;
    store.hubCount = 0;
    store.brandCount = 0;
    store.organizationCount = 0;
    store.noAccess = false;
    store.canRefundOrders = false;
  },
  updateUser(store, data) {
    store.id = data.id;
    store.firstName = data.first_name;
    store.lastName = data.last_name;
    store.email = data.email;
    store.phone = data.phone;
    store.smsEnabled = data.sms_enabled;
    store.authenticated = true;
    store.kitchenIds = data.fulfillmentCenterIds;
    store.hubIds = data.facilityIds;
    store.brandIds = data.brandIds;
    store.organizationIds = data.organizationIds || [];
    store.noAccess = false;
    store.canRefundOrders = !!data.can_refund_orders;
    if (data.role === 'admin') {
      $cookies.set('zdm_role', 'admin');
    } else if (
      data.role === 'portal' &&
      data.facilityIds &&
      data.facilityIds.length
    ) {
      $cookies.set('zdm_role', 'facility_admin');
    } else if (data.role === 'portal') {
      $cookies.set('zdm_role', 'portal');
    } else {
      store.authenticated = false;
      store.noAccess = true;
    }
  },
  toggleSidebar(store) {
    store.sidebarActive = !store.sidebarActive;
  },
  hideSidebar(store) {
    store.sidebarActive = false;
  },
  updateFilterPage(store, data) {
    store.filterPage = data;
  },
  resetFilters(store) {
    store.filters = initialFilter;
  },
  updateFilters(store, data) {
    store.filters = data;
  },
  resetNoAccess(store) {
    store.noAccess = false;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
